import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, Printer } from "lucide-react";
import { usePortalAuth } from "@/components/portal/PortalAuth";
import { site, fullAddress } from "@/config/site";
import { SEO } from "@/components/site/SEO";

const gbp = (n) => `£${Number(n || 0).toFixed(2)}`;

export default function InvoicePrintPage() {
  const { id } = useParams();
  const { user, api } = usePortalAuth();
  const navigate = useNavigate();
  const [inv, setInv] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    if (user === false) navigate("/portal/login", { replace: true });
  }, [user, navigate]);

  useEffect(() => {
    if (user && user.role !== "installer")
      api.get(`/invoices/${id}`).then((r) => setInv(r.data)).catch(() => setMissing(true));
  }, [user, api, id]);

  if (user === false || (user && user.role === "installer")) return null;
  if (missing)
    return (
      <div className="min-h-screen bg-warm-50 flex items-center justify-center px-4">
        <div className="rounded-2xl border border-warm-200 bg-white p-10 text-center shadow-sm" data-testid="invoice-missing">
          <p className="font-serif text-xl text-slate-900 mb-2">Invoice not found</p>
          <a href="/portal/invoices" className="text-sm font-semibold text-navy-900 hover:underline">Back to invoices</a>
        </div>
      </div>
    );
  if (!inv)
    return (
      <div className="min-h-screen bg-warm-50 flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-navy-900" />
      </div>
    );

  const o = inv.order || {};
  const paid = (o.deposit_paid || 0) + (o.balance_paid || 0);
  const outstanding = Math.max(0, (o.deposit_required || 0) - (o.deposit_paid || 0)) + Math.max(0, (o.balance_due || 0) - (o.balance_paid || 0));

  return (
    <div className="min-h-screen bg-warm-50 py-8 px-4 print:bg-white print:py-0" data-testid="invoice-print-page">
      <SEO title={`Invoice ${inv.ref} | My Blinds Company`} description="Invoice." path={`/portal/invoices/${id}/print`} />
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center justify-between mb-6 no-print">
          <a href="/portal/invoices" className="inline-flex items-center gap-2 text-sm font-medium text-navy-900 hover:text-navy-800">
            <ArrowLeft className="h-4 w-4" /> Back to invoices
          </a>
          <button
            type="button"
            data-testid="invoice-print-btn"
            onClick={() => window.print()}
            className="inline-flex items-center gap-2 rounded-full bg-navy-900 px-6 py-2.5 text-xs font-semibold text-white hover:bg-navy-800"
          >
            <Printer className="h-4 w-4" /> Print / Save as PDF
          </button>
        </div>

        <div className="rounded-[1.5rem] border border-warm-200 bg-white shadow-xl overflow-hidden print:shadow-none print:border-0 print:rounded-none" data-testid="invoice-document">
          <div className="bg-navy-950 text-white p-8 print:bg-white print:text-slate-900 print:border-b print:border-warm-200">
            <div className="flex items-start justify-between gap-4">
              <div>
                <img src="/logo.png" alt="My Blinds Company logo" className="h-11 w-auto rounded bg-white p-0.5 mb-3" />
                <p className="text-xs text-white/60 print:text-slate-500">{site.legalName}<br />{fullAddress}</p>
              </div>
              <div className="text-right">
                <p className="text-[11px] uppercase tracking-[0.25em] text-white/50 print:text-slate-500 mb-1">Invoice</p>
                <h1 className="font-serif text-3xl" data-testid="invoice-ref">{inv.ref}</h1>
                <p className="text-xs text-white/60 print:text-slate-500 mt-2">
                  {new Date(inv.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
                  {inv.order_ref ? <><br />Order {inv.order_ref}</> : null}
                </p>
              </div>
            </div>
          </div>

          <div className="p-8">
            <div className="mb-7">
              <p className="text-[11px] uppercase tracking-wider text-slate-400 mb-1.5">Invoice to</p>
              <p className="text-sm font-semibold text-slate-900" data-testid="invoice-customer">{inv.customer?.name || "—"}</p>
              {inv.customer?.cust_ref && <p className="text-xs text-slate-500">{inv.customer.cust_ref}</p>}
              {inv.customer?.address && <p className="text-xs text-slate-500">{inv.customer.address}</p>}
              {inv.customer?.phone && <p className="text-xs text-slate-500">{inv.customer.phone}</p>}
              {inv.customer?.email && <p className="text-xs text-slate-500">{inv.customer.email}</p>}
            </div>

            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-slate-400 border-b border-warm-200">
                  <th className="pb-3 font-semibold">Item</th>
                  <th className="pb-3 font-semibold text-center">Qty</th>
                  <th className="pb-3 font-semibold text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {(o.items || []).map((i, idx) => (
                  <tr key={idx} className="border-b border-warm-200/70">
                    <td className="py-3.5 text-slate-800">{i.description}</td>
                    <td className="py-3.5 text-center text-slate-600">{i.qty}</td>
                    <td className="py-3.5 text-right text-slate-800">{gbp(i.price * i.qty)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="mt-5 space-y-1.5 text-sm">
              <div className="flex justify-between text-lg font-semibold text-slate-900 border-t border-warm-200 pt-3">
                <span>Total</span><span data-testid="invoice-total">{gbp(o.total)}</span>
              </div>
              <div className="flex justify-between text-slate-600">
                <span>Deposit paid</span><span>{gbp(o.deposit_paid)} of {gbp(o.deposit_required)}</span>
              </div>
              <div className="flex justify-between text-slate-600">
                <span>Balance paid</span><span>{gbp(o.balance_paid)} of {gbp(o.balance_due)}</span>
              </div>
              <div className={`flex justify-between font-semibold pt-1 ${outstanding > 0 ? "text-amber-700" : "text-emerald-700"}`}>
                <span>{outstanding > 0 ? "Outstanding" : "Status"}</span>
                <span data-testid="invoice-outstanding">{outstanding > 0 ? gbp(outstanding) : "Paid in full"}</span>
              </div>
            </div>

            <div className="mt-8 border-t border-warm-200 pt-5 text-xs leading-relaxed text-slate-500">
              <p className="font-semibold text-slate-700 mb-1">How to pay</p>
              <p>Call us on {site.phone.display} to pay by card, or email {site.email} for bank transfer details. Please quote invoice {inv.ref}.</p>
              <p className="mt-3">{site.legalName} · {fullAddress} · {site.phone.display}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
